import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';
import StripePaginationResult from 'src/app/core/domain/stripe_pagination_result';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';
import { GenericException } from 'src/app/core/logic/exception';

import { StripeBalanceModel } from '../../../ui/dashboard/routes/account/routes/account-subscription/account-subscription.component';
import { DietitianId } from '../domain/dietitian';
import {
  Invoice,
  InvoiceProps,
  InvoiceStatus,
} from '../domain/subscription/invoice';
import {
  PaymentMethod,
  PaymentMethodProps,
  PaymentMethodType,
} from '../domain/subscription/payment_method';
import {
  StripeProductId,
  StripeSubscriptionId,
  Subscription,
  SubscriptionStatus,
} from '../domain/subscription/subscription';

export interface SubscriptionSchema {
  dietitianId: string;
  stripeSubscriptionId: string | null;
  stripeProductId: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  status: SubscriptionStatus;
  periodStart: Timestamp;
  periodEnd: Timestamp;
  paymentMethod: PaymentMethodSchema | null;
  invoiceActionRequired: string | null;
  maxPeriodEnd: Timestamp | null;
}

export interface PaymentMethodSchema {
  id: string;
  type: PaymentMethodType;
  owner: string | null;
  cardBrand: string | null;
  cardLast4Digits: string | null;
  sepaBankCode: string | null;
  expMonth: number | null;
  expYear: number | null;
  default: boolean;
}

export interface StripePrice {
  id: string;
  type: string;
  name: string;
  amount: number;
  currency: string;
  period: string;
  tax: string;
  product?: StripeProduct | undefined;
}

export interface StripeProduct {
  id: string;
  name: string;
  description?: string | undefined;
  icon?: string | undefined;
  color?: string | undefined;
  backgroundColor?: string | undefined;
  prices: StripePrice[];
  defaultPrice?: string | undefined;
}

export interface StripeInvoice {
  id: string;
  status: string;
}

@Injectable()
export class SubscriptionRepository {
  private readonly createSubscriptionCheckoutSessionFromCloud: (
    data: unknown,
  ) => Observable<string>;

  private readonly createBuyInAppCheckoutSessionFromCloud: (
    data: unknown,
  ) => Observable<string>;

  private readonly createUpdatePaymentMethodCheckoutSessionFromCloud: (
    data: unknown,
  ) => Observable<string>;

  private readonly updateSubscriptionPlanFromCloud: (
    data: unknown,
  ) => Observable<string>;

  private readonly cancelSubscriptionsFromCloud: (
    data: unknown,
  ) => Observable<void>;

  private readonly subscribeFeatureFromCloud: (
    data: unknown,
  ) => Observable<void>;

  private readonly subscribeBundledFeaturesFromCloud: (
    data: unknown,
  ) => Observable<void>;

  private readonly defaultPaymentMethodFromCloud: (
    data: unknown,
  ) => Observable<void>;

  private readonly cancelSubscriptionFeatureFromCloud: (
    data: unknown,
  ) => Observable<void>;

  private readonly getInvoicesFromCloud: (
    data: unknown,
  ) => Observable<StripePaginationResult<{ [key: string]: unknown }>>;

  private readonly getBalancesFromCloud: (
    data: unknown,
  ) => Observable<{ [key: string]: unknown }[]>;

  private readonly getStripeProductFromCloud: (
    data: unknown,
  ) => Observable<StripeProduct>;

  private readonly getStripeProductBySubscriptionFromCloud: (
    data: unknown,
  ) => Observable<StripePrice[]>;

  private readonly getStripePaymentMethodsFromCloud: (
    data: unknown,
  ) => Observable<PaymentMethod[]>;

  private readonly getLastStripeInvoiceBySubscriptionFromCloud: (
    data: unknown,
  ) => Observable<{ [key: string]: unknown }>;

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
  ) {
    this.createSubscriptionCheckoutSessionFromCloud =
      this.functions.httpsCallable<unknown, string>(
        'subscription-createSubscriptionCheckoutSession',
      );

    this.createBuyInAppCheckoutSessionFromCloud = this.functions.httpsCallable<
      unknown,
      string
    >('subscription-createBuyInAppCheckoutSession');

    this.createUpdatePaymentMethodCheckoutSessionFromCloud =
      this.functions.httpsCallable<unknown, string>(
        'subscription-createUpdatePaymentMethodCheckoutSession',
      );

    this.updateSubscriptionPlanFromCloud = this.functions.httpsCallable<
      unknown,
      string
    >('subscription-updateSubscriptionPlan');

    this.cancelSubscriptionsFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('subscription-cancelSubscriptions');

    this.subscribeFeatureFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('subscription-subscribeFeature');

    this.subscribeBundledFeaturesFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('subscription-subscribeBundledFeatures');

    this.defaultPaymentMethodFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('subscription-defaultPaymentMethod');

    this.cancelSubscriptionFeatureFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('subscription-cancelSubscriptionFeature');

    this.getInvoicesFromCloud = this.functions.httpsCallable<
      unknown,
      StripePaginationResult<{ [key: string]: unknown }>
    >('subscription-getInvoices');

    this.getBalancesFromCloud = this.functions.httpsCallable<
      unknown,
      { [key: string]: unknown }[]
    >('subscription-getStripeBalances');

    this.getStripeProductFromCloud = this.functions.httpsCallable<
      unknown,
      StripeProduct
    >('subscription-getStripeProduct');

    this.getStripeProductBySubscriptionFromCloud = this.functions.httpsCallable<
      unknown,
      StripePrice[]
    >('subscription-getStripeProductBySubscription');

    this.getStripePaymentMethodsFromCloud = this.functions.httpsCallable<
      unknown,
      PaymentMethod[]
    >('subscription-getStripePaymentMethods');

    this.getLastStripeInvoiceBySubscriptionFromCloud =
      this.functions.httpsCallable<unknown, { [key: string]: unknown }>(
        'subscription-getLastInvoiceBySubscription',
      );
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<SubscriptionSchema>(
      'subscriptions',
      queryFn,
    );
  }

  toSchema(subscription: Subscription): SubscriptionSchema {
    return {
      dietitianId: subscription.dietitianId.id.toString(),
      stripeSubscriptionId:
        subscription.stripeSubscriptionId?.id.toString() ?? null,
      stripeProductId: subscription.stripeProductId?.id.toString() ?? null,
      createdAt:
        subscription.createdAt !== undefined
          ? Timestamp.fromDate(subscription.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: subscription.status,
      periodStart: Timestamp.fromDate(subscription.periodStart),
      periodEnd: Timestamp.fromDate(subscription.periodEnd),
      paymentMethod: subscription.paymentMethod
        ? {
            id: subscription.paymentMethod.id,
            type: subscription.paymentMethod.type,
            owner: subscription.paymentMethod.owner ?? null,
            cardBrand: subscription.paymentMethod.cardBrand ?? null,
            cardLast4Digits: subscription.paymentMethod.cardLast4Digits ?? null,
            sepaBankCode: subscription.paymentMethod.sepaBankCode ?? null,
            expMonth: subscription.paymentMethod.expMonth ?? null,
            expYear: subscription.paymentMethod.expYear ?? null,
            default: subscription.paymentMethod.default,
          }
        : null,
      invoiceActionRequired: subscription.invoiceActionRequired ?? null,
      maxPeriodEnd: subscription.maxPeriodEnd
        ? Timestamp.fromDate(subscription.maxPeriodEnd)
        : null,
    };
  }

  fromSchema(schema: SubscriptionSchema, id: string): Subscription {
    return Subscription.create(
      {
        dietitianId: DietitianId.create(new UniqueEntityID(schema.dietitianId)),
        stripeSubscriptionId: schema.stripeSubscriptionId
          ? StripeSubscriptionId.create(
              new UniqueEntityID(schema.stripeSubscriptionId),
            )
          : undefined,
        stripeProductId: schema.stripeProductId
          ? StripeProductId.create(new UniqueEntityID(schema.stripeProductId))
          : undefined,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt.toDate(),
        status: schema.status,
        periodStart: schema.periodStart.toDate(),
        periodEnd: schema.periodEnd.toDate(),
        paymentMethod: schema.paymentMethod
          ? PaymentMethod.create({
              id: schema.paymentMethod.id,
              type: schema.paymentMethod.type,
              owner: schema.paymentMethod.owner ?? undefined,
              cardBrand: schema.paymentMethod.cardBrand ?? undefined,
              cardLast4Digits:
                schema.paymentMethod.cardLast4Digits ?? undefined,
              sepaBankCode: schema.paymentMethod.sepaBankCode ?? undefined,
              expMonth: schema.paymentMethod.expMonth ?? undefined,
              expYear: schema.paymentMethod.expYear ?? undefined,
              default: schema.paymentMethod.default,
            })
          : undefined,
        invoiceActionRequired: schema.invoiceActionRequired ?? undefined,
        maxPeriodEnd: schema.maxPeriodEnd?.toDate() ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  async findDietitianSubscriptions(
    dietitianId: string,
  ): Promise<Subscription[]> {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref
          .where('dietitianId', '==', dietitianId)
          .orderBy('createdAt', 'desc'),
      ).get(),
    );

    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async findDietitianLatestSubscription(
    dietitianId: string,
  ): Promise<Subscription | undefined> {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref
          .where('dietitianId', '==', dietitianId)
          .orderBy('createdAt', 'desc')
          .limit(1),
      ).get(),
    );

    if (snap.size == 0) return undefined;

    return this.fromSchema(snap.docs[0].data(), snap.docs[0].id);
  }

  listenSubscription(
    subscriptionId: string,
  ): Observable<SubscriptionSchema | undefined> {
    return this.collection().doc(subscriptionId).valueChanges();
  }

  async findDietitianRunningSubscription(
    dietitianId: string,
  ): Promise<Subscription | undefined> {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref
          .where('dietitianId', '==', dietitianId)
          .where('status', 'in', [
            SubscriptionStatus.Active,
            SubscriptionStatus.Trialing,
            SubscriptionStatus.PendingCancellation,
            SubscriptionStatus.Unpaid,
            SubscriptionStatus.PastDue,
          ])
          .where('periodEnd', '>=', new Date())
          .orderBy('periodEnd', 'desc')
          .orderBy('createdAt', 'desc'),
      ).get(),
    );

    if (snap.size == 0) return undefined;

    const docs = snap.docs.sort((a, b) =>
      a.data().status.localeCompare(b.data().status),
    );

    return this.fromSchema(docs[0].data(), docs[0].id);
  }

  async createSubscriptionCheckoutSession(
    priceId: string,
    sponsorship: string | undefined,
    sponsorshipCode: string | undefined,
    successUrl: string,
    cancelUrl: string,
  ): Promise<string> {
    try {
      const checkoutUrl = await firstValueFrom(
        this.createSubscriptionCheckoutSessionFromCloud({
          priceId,
          sponsorship,
          sponsorshipCode,
          successUrl,
          cancelUrl,
        }),
      );

      return checkoutUrl;
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async createBuyInAppCheckoutSession(
    productId: string,
    successUrl: string,
    cancelUrl: string,
  ): Promise<string> {
    try {
      const checkout = await firstValueFrom(
        this.createBuyInAppCheckoutSessionFromCloud({
          productId,
          successUrl,
          cancelUrl,
        }),
      );
      return checkout;
    } catch (e) {
      throw new GenericException(e);
    }
  }

  async createUpdatePaymentMethodCheckoutSession(
    successUrl: string,
    cancelUrl: string,
    metadata: any,
  ): Promise<string> {
    try {
      const checkoutUrl = await firstValueFrom(
        this.createUpdatePaymentMethodCheckoutSessionFromCloud({
          successUrl,
          cancelUrl,
          metadata,
        }),
      );

      return checkoutUrl;
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async updateSubscriptionPlan(
    subscriptionId: string,
    priceId: string,
  ): Promise<void> {
    try {
      await firstValueFrom(
        this.updateSubscriptionPlanFromCloud({
          subscriptionId,
          priceId,
        }),
      );
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async cancelSubscriptions(): Promise<void> {
    try {
      await firstValueFrom(this.cancelSubscriptionsFromCloud({}));
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async getInvoices(
    pageSize: number,
    startingAfter: string | null,
    endingBefore: string | null,
  ): Promise<StripePaginationResult<Invoice>> {
    try {
      const result = await firstValueFrom(
        this.getInvoicesFromCloud({
          pageSize,
          startingAfter,
          endingBefore,
        }),
      );
      return {
        ...result,
        results: result.results.map((res) =>
          Invoice.create(
            {
              createdAt: new Date(res['createdAt'] as string),
              status: res['status'] as InvoiceStatus,
              amount: res['amount'] as number,
              pdfUrl: res['pdfUrl'] as string | undefined,
              payUrl: res['payUrl'] as string | undefined,
              number: res['number'] as string | undefined,
              receiptNumber: res['receiptNumber'] as string | undefined,
              attempted: res['attempted'] as boolean,
              attemptCount: res['attemptCount'] as number,
              amountDue: res['amountDue'] as number,
              amountPaid: res['amountPaid'] as number,
              paid: res['paid'] as boolean,
              paymentIntent: res['paymentIntent'] as any,
            },
            new UniqueEntityID(res['id'] as string),
          ),
        ),
      };
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async getBalances(): Promise<StripeBalanceModel[]> {
    try {
      const results = await firstValueFrom(this.getBalancesFromCloud({}));
      return results.map((res) => {
        const invoice = res['invoice'] as { [key: string]: unknown };
        const invoiceId = invoice ? invoice['id'] : undefined;
        const invoiceProps = invoice ? invoice['props'] : undefined;
        return {
          id: res['id'] as string,
          date: new Date(res['date'] as number),
          type: res['type'] as string,
          description: res['description'] as string | null,
          amount: res['amount'] as number,
          currency: res['currency'] as string,
          debitCredit: res['debitCredit'] as number,
          invoice: invoice
            ? Invoice.create(
                invoiceProps as InvoiceProps,
                new UniqueEntityID(invoiceId as string),
              )
            : null,
        } as StripeBalanceModel;
      });
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async createSubscription(subscription: Subscription): Promise<Subscription> {
    const schema = this.toSchema(subscription);

    return this.collection()
      .add(schema)
      .then((ref) => this.fromSchema(schema, ref.id));
  }

  async getStripeProduct(stripeProductId: string): Promise<StripeProduct> {
    return firstValueFrom(
      this.getStripeProductFromCloud({ productId: stripeProductId }),
    );
  }

  async getStripeProductBySubscription(
    stripeSubscriptionId: string,
  ): Promise<StripePrice[]> {
    return firstValueFrom(
      this.getStripeProductBySubscriptionFromCloud({
        subscriptionId: stripeSubscriptionId,
      }),
    );
  }

  async cancelSubscriptionFeature(stripeSubscriptionId: string | undefined) {
    try {
      await firstValueFrom(
        this.cancelSubscriptionFeatureFromCloud({ stripeSubscriptionId }),
      );
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const results = await firstValueFrom(
        this.getStripePaymentMethodsFromCloud({}),
      );
      return results.map((res) => {
        const pm = res;
        const pmProps = pm['props'] as PaymentMethodProps;
        return PaymentMethod.create(pmProps);
      });
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async subscribeFeature(featurePriceId: string, coupon: string | undefined) {
    await firstValueFrom(
      this.subscribeFeatureFromCloud({ featurePriceId, coupon }),
    );
  }

  async subscribeBundledFeatures(
    bundledFeaturesPriceId: string,
    coupon: string | undefined,
  ) {
    await firstValueFrom(
      this.subscribeBundledFeaturesFromCloud({
        bundledFeaturesPriceId,
        coupon,
      }),
    );
  }

  async defaultPaymentMethod(paymentMethodId: string) {
    try {
      await firstValueFrom(
        this.defaultPaymentMethodFromCloud({ paymentMethodId }),
      );
    } catch (error) {
      throw new GenericException(error);
    }
  }

  async getLastInvoiceBySubscription(
    subscriptionId: string | undefined,
  ): Promise<{ [key: string]: unknown }> {
    try {
      return await firstValueFrom(
        this.getLastStripeInvoiceBySubscriptionFromCloud({
          subscriptionId,
        }),
      );
    } catch (error) {
      throw new GenericException(error);
    }
  }
}
